/**
 * generatePageGroups
 *
 * Inputs:
 *  - total: total items (not pages)
 *  - perPage: items per page
 *  - selectedPage: 1-based page index
 *
 * Output fields:
 *  - shouldShowBackButton: boolean
 *  - firstPages: left block (either 1..8 expanded, or 1..3 compact, or full range if merged)
 *  - shouldShowMiddleEllipsis: whether to show the ellipsis immediately after firstPages
 *  - middleEllipsisPage?: when true, the page the middle ellipsis should jump to
 *      (by requirement, when firstPages = [1,2,3] this will be 4)
 *  - lastPages: the SECOND block shown (contextual: final pages when left is expanded,
 *               or selected-centered block when left is compact)
 *  - shouldShowEndEllipsis: whether to show an ellipsis after lastPages
 *  - endEllipsisPage?: when true, the page after lastPages (i.e., lastPages[last] + 1)
 *  - shouldShowNextButton: boolean
 *
 * Note: no "middlePages" array is returned. If the UI shows a second block that is the centered
 * selected page group, it will appear in `lastPages`. The UI can use `middleEllipsisPage` and
 * `endEllipsisPage` when rendering ellipsis jump targets.
 */
export interface PageGroups {
  shouldShowBackButton: boolean
  firstPages: number[]
  shouldShowMiddleEllipsis: boolean
  middleEllipsisPage?: number
  lastPages: number[]
  shouldShowEndEllipsis: boolean
  endEllipsisPage?: number
  shouldShowNextButton: boolean
}

// oxlint-disable-next-line complexity
export function generatePageGroups({
  total,
  perPage,
  selectedPage,
}: {
  total: number
  perPage: number
  selectedPage: number
}): PageGroups {
  // sanitize inputs
  total = Math.max(0, Math.floor(Number(total) || 0))
  perPage = Math.max(1, Math.floor(Number(perPage) || 1))
  const totalPages = Math.max(1, Math.ceil(total / perPage))

  // clamp selectedPage
  let sel = Math.floor(Number(selectedPage) || 1)
  if (sel < 1) {
    sel = 1
  }
  if (sel > totalPages) {
    sel = totalPages
  }

  // UI constants (from screenshots)
  const LEFT_EXPANDED = 8 // when selected in 1..8 (or totalPages <= 8) show 1..8
  const LEFT_COMPACT = 3 // otherwise show 1..3
  const RIGHT_COUNT = 3 // when showing final pages, show up to 3
  const AROUND = 1 // centered block size = selected ± AROUND

  const range = (s: number, e: number) => {
    if (e < s) {
      return [] as number[]
    }
    const out: number[] = []

    for (let i = s; i <= e; i++) {
      out.push(i)
    }
    return out
  }

  // Decide first block (left)
  const firstIsExpanded = sel <= LEFT_EXPANDED || totalPages <= LEFT_EXPANDED
  const firstPagesInitial = firstIsExpanded
    ? range(1, Math.min(LEFT_EXPANDED, totalPages))
    : range(1, Math.min(LEFT_COMPACT, totalPages))

  // Candidate for the second block (what we return as lastPages):
  // - If left expanded: show final pages (right-most block)
  // - If left compact: show the centered block around `sel` (selected ±1)
  let secondBlockCandidate: number[] = []
  if (firstIsExpanded) {
    secondBlockCandidate = range(Math.max(1, totalPages - RIGHT_COUNT + 1), totalPages)
  } else {
    const midStart = Math.max(1, sel - AROUND)
    const midEnd = Math.min(totalPages, sel + AROUND)
    secondBlockCandidate = range(midStart, midEnd)
  }

  // Merge / adjust when blocks touch or overlap:
  const firstLastOrig = firstPagesInitial.at(-1) ?? 0
  const secondFirstOrig = secondBlockCandidate[0] ?? totalPages + 1
  const secondLastOrig = secondBlockCandidate.at(-1) ?? 0

  let firstPages = [...firstPagesInitial]
  let lastPages = [...secondBlockCandidate]

  // If second block touches/overlaps first, merge into one block (no second block)
  if (secondFirstOrig <= firstLastOrig + 1) {
    const newEnd = Math.max(firstLastOrig, secondLastOrig)
    firstPages = range(1, newEnd)
    lastPages = []
  }

  // If after any merging first and (would be) last touch/overlap, collapse to full range
  const firstLast = firstPages.at(-1) ?? 0
  const lastFirst = lastPages[0] ?? totalPages + 1
  if (firstLast >= lastFirst - 1) {
    firstPages = range(1, totalPages)
    lastPages = []
  }

  // navigation buttons
  const shouldShowBackButton = sel > 1
  const shouldShowNextButton = sel < totalPages

  // Middle ellipsis: the ellipsis immediately AFTER firstPages (between first and second block)
  // Per your instruction: when firstPages are compact (1..3) the middle ellipsis target should be 4.
  let shouldShowMiddleEllipsis = false
  let middleEllipsisPage: number | undefined = undefined
  if (lastPages.length > 0) {
    // There is a second block; show "middle" ellipsis if there's a gap between first and second
    // oxlint-disable-next-line unicorn/no-lonely-if, unicorn/prefer-at
    if (firstPages[firstPages.length - 1] + 1 < lastPages[0]) {
      shouldShowMiddleEllipsis = true
      // If first block is compact (1..3), middle ellipsis should point at 4 (next after 1,2,3).
      // Otherwise (first expanded), the ellipsis after the first block should point to firstPages[last]+1.
      middleEllipsisPage = firstIsExpanded
        ? // oxlint-disable-next-line unicorn/prefer-at
          firstPages[firstPages.length - 1] + 1
        : // oxlint-disable-next-line unicorn/prefer-at
          firstPages[firstPages.length - 1] + 1
      // note: when firstIsExpanded, middleEllipsisPage is firstPages.last + 1 (e.g., 9)
      // when firstIsCompact, firstPages.last = 3 so middleEllipsisPage = 4, as requested
    }
  }

  // End ellipsis: ellipsis AFTER the second block (if there are pages beyond it)
  let shouldShowEndEllipsis = false
  let endEllipsisPage: number | undefined = undefined
  // oxlint-disable-next-line unicorn/prefer-at
  if (lastPages.length > 0 && lastPages[lastPages.length - 1] < totalPages) {
    shouldShowEndEllipsis = true
    // oxlint-disable-next-line unicorn/prefer-at
    endEllipsisPage = lastPages[lastPages.length - 1] + 1
  }

  // If there's no second block (merged into first), we might still show an "end" ellipsis
  // when first block doesn't reach the end but we didn't set a second block (this happens when
  // first was expanded and we merged incorrectly earlier). Guard: if lastPages is empty but first doesn't cover end,
  // then show end ellipsis pointing to firstPages[last] + 1 (this represents the next hidden pages).
  // oxlint-disable-next-line unicorn/prefer-at
  if (lastPages.length === 0 && firstPages[firstPages.length - 1] < totalPages) {
    // after merging, if there are still pages after firstPages, show a single end ellipsis
    shouldShowEndEllipsis = true
    // oxlint-disable-next-line unicorn/prefer-at
    endEllipsisPage = firstPages[firstPages.length - 1] + 1
    // middle ellipsis should be false in this state
    shouldShowMiddleEllipsis = false
    middleEllipsisPage = undefined
  }

  // Final dedupe / safety
  const dedupe = (arr: number[]) => Array.from(new Set(arr)).sort((a, b) => a - b)
  firstPages = dedupe(firstPages)
  lastPages = dedupe(lastPages)

  // Ensure middleEllipsisPage is exactly 4 when firstPages === [1,2,3] and we set it above.
  if (
    firstPages.length === 3 &&
    firstPages[0] === 1 &&
    firstPages[1] === 2 &&
    firstPages[2] === 3
  ) {
    // oxlint-disable-next-line unicorn/no-lonely-if
    if (shouldShowMiddleEllipsis) {
      middleEllipsisPage = 4
    }
  }

  return {
    shouldShowBackButton,
    firstPages,
    shouldShowMiddleEllipsis,
    middleEllipsisPage,
    lastPages,
    shouldShowEndEllipsis,
    endEllipsisPage,
    shouldShowNextButton,
  }
}
